import "./Admin.css";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import jwt_decode from "jwt-decode";
import setAuthToken from "../../utils/setAuthToken";
import AdminLeaderboard from "./AdminLeaderboard";
import AdminMusic from "./AdminMusic";
import AdminDashboard from "./AdminDashboard";
import { setCurrentUser, logoutUser } from "../../actions/authActions";

const api = process.env.REACT_APP_API_URL;

function branchStorageKey(userId) {
  return `bealyo.admin.lastBranch.${userId}`;
}

function getInitialBranch(userId, circles) {
  let savedId = "";

  try {
    savedId = localStorage.getItem(branchStorageKey(userId)) || "";
  } catch {
    // Continue normally when browser storage is unavailable.
  }

  return circles.some((circle) => circle._id === savedId)
    ? savedId
    : circles[0]?._id || "";
}

export default function AdminPortal() {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [account, setAccount] = useState(null);
  const [circles, setCircles] = useState([]);
  const [circleId, setCircleId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const musicPanelRef = useRef(null);
  const [musicScrollTrigger, setMusicScrollTrigger] = useState(0);

  const selectedCircle = circles.find((circle) => circle._id === circleId);

  // Restore an existing session and check current admin access.
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("jwtToken");

    if (!token) {
      setLoading(false);
      return;
    }

    axios
      .get(api + "users/admin/circles", {
        headers: { Authorization: token },
      })
      .then(({ data }) => {
        if (cancelled) return;

        setAccount(data.user);
        setCircles(data.circles);
        setCircleId(getInitialBranch(data.user.id, data.circles));
      })
      .catch((err) => {
        if (cancelled) return;

        if (err.response?.status === 401) {
          dispatch(logoutUser());
        }

        setError(
          err.response?.data?.message ||
            "Unable to load admin access. Please try signing in.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (activeSection !== "music" || loading || !circleId) return;

    const frame = requestAnimationFrame(() => {
      musicPanelRef.current?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [activeSection, circleId, loading, musicScrollTrigger, mobileMenuOpen]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const loginResponse = await axios.post(api + "users/login", {
        email: email.trim().toLowerCase(),
        password,
      });

      const token = loginResponse.data.token;

      if (typeof token !== "string") {
        throw new Error("Missing login token");
      }

      // Confirm admin access before saving the new session.
      const { data } = await axios.get(api + "users/admin/circles", {
        headers: { Authorization: token },
      });

      const decoded = jwt_decode(token.replace(/^Bearer\s+/i, ""));

      localStorage.setItem("jwtToken", token);
      setAuthToken(token);
      dispatch(setCurrentUser(decoded));

      setAccount(data.user);
      setCircles(data.circles);
      setCircleId(getInitialBranch(data.user.id, data.circles));
      setPassword("");
    } catch (err) {
      const data = err.response?.data;

      setError(
        data?.message ||
          data?.emailnotfound ||
          data?.passwordincorrect ||
          data?.email ||
          data?.password ||
          "Unable to sign in. Please check your details and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const changeBranch = (nextId) => {
    if (!circles.some((circle) => circle._id === nextId)) return;

    setCircleId(nextId);

    try {
      localStorage.setItem(branchStorageKey(account.id), nextId);
    } catch {
      // Branch selection still works without persistence.
    }
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    setAccount(null);
    setCircles([]);
    setCircleId("");
    setPassword("");
    setError("");
    setActiveSection("dashboard");
    setMobileMenuOpen(false);
  };

  return (
    <main className="ba-admin">
      {loading ? (
        <div className="ba-loading" role="status">
          Loading your workspace…
        </div>
      ) : !account ? (
        <div className="ba-login-wrap">
          <form className="ba-login" onSubmit={handleLogin}>
            <div className="ba-brand">
              <img src="/logo.png" alt="Bealyo" className="ba-logo" />
              <span>Admin</span>
            </div>

            <h1>Welcome back.</h1>
            <p>Sign in with your Bealyo account to manage your community.</p>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <label htmlFor="admin-email">Email address</label>
            <input
              id="admin-email"
              className="form-control"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />

            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              className="form-control"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />

            <button className="btn btn-primary ba-login-submit" type="submit">
              Sign in to your workspace
            </button>
          </form>
        </div>
      ) : (
        <div className="ba-shell">
          <aside
            className={`ba-sidebar ${mobileMenuOpen ? "is-menu-open" : ""}`}
          >
            <div className="ba-sidebar-header">
              <div className="ba-brand">
                <img src="/logo.png" alt="Bealyo" className="ba-logo" />
                <span>Admin</span>
              </div>

              <button
                type="button"
                className="ba-mobile-menu-toggle"
                aria-label={
                  mobileMenuOpen ? "Close navigation" : "Open navigation"
                }
                aria-expanded={mobileMenuOpen}
                aria-controls="admin-navigation"
                onClick={() => setMobileMenuOpen((previous) => !previous)}
              >
                {mobileMenuOpen ? (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="m6 6 12 12M18 6 6 18" />
                  </svg>
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
            <div className="ba-nav-caption">WORKSPACE</div>

            <nav
              id="admin-navigation"
              className="ba-navigation"
              aria-label="Admin sections"
            >
              {[
                ["dashboard", "Overview", "◫"],
                ["music", "Music requests", "♫"],
                ["leaderboard", "Leaderboard", "▥"],
              ].map(([id, label, icon]) => (
                <button
                  key={id}
                  type="button"
                  className={`ba-nav-item ${
                    activeSection === id ? "is-active" : ""
                  }`}
                  aria-current={activeSection === id ? "page" : undefined}
                  onClick={() => {
                    setActiveSection(id);
                    setMobileMenuOpen(false);

                    if (id === "music") {
                      setMusicScrollTrigger((value) => value + 1);
                    }
                  }}
                >
                  <span className="ba-nav-icon" aria-hidden="true">
                    {icon}
                  </span>
                  {label}
                </button>
              ))}
            </nav>

            <div className="ba-sidebar-bottom">
              {selectedCircle?.circle_logo && (
                <div className="ba-branch-brand">
                  <img
                    src={
                      "https://bealyo-upload-files.s3.us-east-2.amazonaws.com/" +
                      selectedCircle.circle_logo
                    }
                    alt={`${selectedCircle.name} logo`}
                    className="ba-branch-logo"
                  />
                  <span>{selectedCircle.name}</span>
                </div>
              )}
              <div className="ba-account">
                <div className="ba-avatar" aria-hidden="true">
                  {(account.name || "A").slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <strong>{account.name || "Admin"}</strong>
                  <small>Branch management</small>
                </div>
              </div>
              <a href="/" className="ba-back-to-site">
                ← Back to website
              </a>

              <button
                type="button"
                className="ba-signout"
                onClick={handleLogout}
              >
                Sign out
              </button>
            </div>
          </aside>

          <div className="ba-main">
            <header className="ba-topbar">
              <div className="ba-breadcrumb">
                Workspace <span>/</span>{" "}
                {
                  {
                    dashboard: "Overview",
                    music: "Music requests",
                    leaderboard: "Leaderboard",
                  }[activeSection]
                }
              </div>

              <div className="ba-branch-picker">
                <label htmlFor="admin-circle">Branch</label>
                <select
                  id="admin-circle"
                  className="form-control"
                  value={circleId}
                  onChange={(event) => changeBranch(event.target.value)}
                >
                  {circles.map((circle) => (
                    <option key={circle._id} value={circle._id}>
                      {circle.name}
                    </option>
                  ))}
                </select>
              </div>
            </header>

            <div className="ba-content">
              <div className="ba-page-heading">
                <div>
                  <div className="ba-eyebrow">YOUR COMMUNITY</div>
                  <h1>{selectedCircle?.name || "Your workspace"}</h1>
                  <p>
                    {
                      {
                        dashboard: "A clear view of your branch and community.",
                        music: "Give your community a voice in the music.",
                        leaderboard:
                          "Shape the experience on your branch’s screen.",
                      }[activeSection]
                    }
                  </p>
                </div>
                <span className="ba-badge">Admin workspace</span>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {!circleId && <p>No branches available for this account.</p>}

              {activeSection === "dashboard" && circleId && (
                <AdminDashboard
                  key={circleId}
                  circleId={circleId}
                  onNavigate={setActiveSection}
                />
              )}

              {activeSection === "music" && circleId && (
                <div ref={musicPanelRef} className="ba-music-scroll-target">
                  <AdminMusic key={circleId} circleId={circleId} />
                </div>
              )}

              {activeSection === "leaderboard" && circleId && (
                <AdminLeaderboard key={circleId} circleId={circleId} />
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
